import { computed, inject, unref } from 'vue';
import type { Ref } from 'vue';
import colors from 'tailwindcss/colors';
import { watchEffect, ref } from 'vue';
import makeSvgIdsUnique from '../lib/makeSvgIdsUnique';
// import DOMPurify from 'dompurify';
import expandUploadPath from './expandUploadPath';
import bugAlert from './bugAlert';
import useThemeChange from './useThemeChange';

/**
 * For each shade of a given color palette, replaces the hex code with the CSS variable
 *
 * @param str The SVG source
 * @param c The colors to replace, e.g. { primary: 'red' }
 */
const replaceColors = (
  str: string,
  c: {
    primary?: string | null;
    secondary?: string | null;
    gray?: string | null;
  } = {},
  elRef = ref() as Ref<HTMLElement | undefined>
) => {
  let res = str;

  const el = elRef?.value;

  for (const [colorKey, colorValue] of Object.entries(c)) {
    if (!colorValue) continue;

    for (const [shade, hex] of Object.entries(colors[colorValue])) {
      const cssVar = `--color-${colorKey}-${shade}`;
      const rgb = el && getComputedStyle(el).getPropertyValue(cssVar);
      const val = rgb ? `rgb(${rgb})` : `rgb(var(${cssVar}))`;

      res = res.replace(new RegExp(hex as string, 'gi'), val);
    }
  }

  return res;
};

export default function useThemedSvg(
  props: {
    src: string;
  },
  { transformClass = false, elRef = ref() as Ref<HTMLElement | undefined> } = {}
) {
  const svgRaw = ref('');
  const svg = ref('');
  const src = computed(() => expandUploadPath(props.src));
  const audioDoc = inject('audioDoc') as boolean;
  const wordDoc = unref(inject('wordDoc')) as boolean;

  const fetchSvg = async () => {
    if (!src.value || !src.value.startsWith('http')) {
      console.log(`Invalid URL: ${src.value}`);
      return;
    } else if (audioDoc || (wordDoc && !elRef.value)) {
      // skip fetching/processing themed SVGs when generating audio scripts
      return;
    }

    try {
      let url = unref(src);
      const origin = window.location.origin;

      // avoid CORS requests when we can
      const proxyAvailable =
        origin.includes('vinciworks') ||
        origin === 'https://uk.onlinecompliance.org';

      if (proxyAvailable) {
        url = url.replace(
          'https://vinciworks-lms-files.s3.eu-west-2.amazonaws.com',
          `${origin}/awscontent`
        );
      }

      const r = await fetch(url);
      const raw = await r.text();

      if (r.ok && raw.includes('<svg')) {
        svgRaw.value = raw;
      } else {
        svgRaw.value = '';
        bugAlert(`Could not find SVG: ${url}`);
      }
    } catch (e) {
      console.error(e);
      bugAlert(`Could not fetch SVG: ${src.value}`);
    }
  };

  watchEffect(fetchSvg);

  const processRawSvg = () => {
    let out = svgRaw.value;

    if (typeof out !== 'string' || out.length === 0) {
      return;
    }

    // get primary and secondary color in the SVG by matching the file name, e.g. `my-image.p-blue.svg`
    const primary = src.value.match(/\.(p|primary)-(\w+)\./)?.[2];
    const secondary = src.value.match(/\.(s|secondary)-(\w+)\./)?.[2];
    const gray = 'gray';

    out = makeSvgIdsUnique(out);
    out = replaceColors(out, { primary, secondary, gray }, elRef);

    if (transformClass) {
      out = out.replace(/ class="/g, ' data-class="');
    }

    // TODO handle use tags securely
    // https://github.com/cure53/DOMPurify/issues/574
    // https://github.com/cure53/DOMPurify/issues/233
    // raw = DOMPurify.sanitize(raw, { ADD_TAGS: ['use'] });

    // remove any xml declaration
    out = out.replace(/<\?xml.*?\?>/, '');

    // ensure raw string has preserveAspectRatio="xMidYMin slice" on the <svg> tag
    const svgTagMatch = out.match(/<svg[^>]*>/);
    if (svgTagMatch && !svgTagMatch[0].includes('preserveAspectRatio')) {
      out = out.replace('<svg ', '<svg preserveAspectRatio="xMidYMin slice" ');
    }

    svg.value = out;
  };

  if (import.meta.hot) {
    import.meta.hot.on('publicwatch:update', async ({ path }) => {
      const name = path.split(/[\\/]/).slice(-1)[0];
      if (!src.value.includes(name)) return;

      await fetch(src.value, { cache: 'reload', mode: 'no-cors' });
      fetchSvg();
    });
  }

  watchEffect(processRawSvg);
  useThemeChange(processRawSvg);

  return svg;
}
